import React, { useState, useEffect, useRef, KeyboardEvent, useMemo, SetStateAction, useCallback, memo, SyntheticEvent } from "react";
import { Slider, InputAdornment, OutlinedInput, Typography, Box } from "@mui/material";
import debounce from "lodash.debounce";
import { useTranslation } from "next-i18next";
import { KEY_ENTER } from "src/consts/keyboard";
import { ILanguage } from "src/consts/locale";
import { formatRangeSliderNumber } from "src/hooks/formatters/number-formatter";
import { NonEmptyArray } from "src/types/common";
import { useRouterLocale } from "src/utils/router/use-router-locale";
import {
    getRangeIndexFromInput,
    getInputValueFromRange,
    getNumberFromInputString,
    isInputValid,
    shouldNegateInputNumber,
} from "./range-slider-utils";

const STEP = 1;
const KEY_FIGURE_TEST_ID = "key-figure";

interface IRangeInputProps {
    ariaLabel: string;
    value: string;
    shouldShowPercentageLabel: boolean;
    language: ILanguage;
    onChange: (value: string) => void;
    onBlur: () => void;
    handleKeyDown: (event: KeyboardEvent<HTMLInputElement>, handler: () => void) => void;
    handleOnFocus: () => void;
}

const RangeInput = ({ ariaLabel, value, shouldShowPercentageLabel, language, onChange, onBlur, handleKeyDown, handleOnFocus }: IRangeInputProps) => {
    return (
        <OutlinedInput
            aria-label={ariaLabel}
            sx={{
                width: {
                    xs: "100%",
                    sm: "150px",
                },
                input: {
                    textAlign: "right",
                    paddingRight: 0,
                    fontSize: "sm",
                },
                paddingRight: shouldShowPercentageLabel ? "19px" : "17px",
                borderRadius: 0.5,
                height: "32px",
            }}
            size="small"
            value={value}
            onChange={event => onChange(event.target.value)}
            onBlur={onBlur}
            onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                handleKeyDown(event, onBlur);
                event.stopPropagation();
            }}
            onFocus={handleOnFocus}
            endAdornment={
                shouldShowPercentageLabel && (
                    <InputAdornment
                        position="end"
                        disableTypography={true}
                        sx={{ marginLeft: language === "de" ? "5px" : "1px", fontSize: "sm", color: "currentColor" }}
                    >
                        %
                    </InputAdornment>
                )
            }
        />
    );
};

export interface IRangeSliderWithInputsProps {
    label: string;
    selectedValues: [number?, number?];
    steps: NonEmptyArray<number>;
    showMarks: boolean;
    onChange: (values: [number, number]) => void;
    shouldShowPercentageLabel: boolean;
    showPositiveLeadingSign: boolean;
}

const areSelectedInputValuesEqual = (prevProps: IRangeSliderWithInputsProps, nextProps: IRangeSliderWithInputsProps) => {
    return prevProps.selectedValues[0] === nextProps.selectedValues[0] && prevProps.selectedValues[1] === nextProps.selectedValues[1];
};

const RangeSliderWithInputs = ({
    label,
    selectedValues,
    steps,
    shouldShowPercentageLabel,
    showMarks,
    showPositiveLeadingSign,
    onChange,
}: IRangeSliderWithInputsProps) => {
    const { t } = useTranslation("date-range");
    const thousandSeparator = t("thousand-separator");
    const { language } = useRouterLocale();
    const minText = "min.";
    const maxText = "max.";

    const firstRangeStepIndex = 0;
    const lastRangeStepIndex = steps.length - 1;
    const firstRangeStep = steps[firstRangeStepIndex];
    const lastRangeStep = steps[lastRangeStepIndex];

    const defaultMinValue = selectedValues[0] ?? firstRangeStep;
    const defaultMaxValue = selectedValues[1] ?? lastRangeStep;

    const defaultMinRangeIndex = getRangeIndexFromInput(defaultMinValue, steps);
    const defaultMaxRangeIndex = getRangeIndexFromInput(defaultMaxValue, steps);

    const [inputValue, setInputValue] = useState<[string, string]>([minText, maxText]);

    const rowRef = useRef<HTMLInputElement>(null);
    // DEV-Note: Using keyboard leads to a later calling of onChangeEnd with the last values on every re-render of the component.
    // Track the focus and blur state before calling onChangeEnd callback.
    const isChangingRangeValueRef = useRef<boolean | null>(true);

    const [rangeIndex, setRangeIndex] = useState<[number, number]>([defaultMinRangeIndex, defaultMaxRangeIndex]);

    const marks = useMemo(() => steps.slice(0, steps.length - 2).map((st, index) => ({ value: index + 1 })), [steps]);

    const getMinValue = useCallback(
        (value: string) => (value === minText || value === "" ? firstRangeStep : getNumberFromInputString(value, thousandSeparator)),
        [firstRangeStep, thousandSeparator]
    );
    const getMaxValue = useCallback(
        (value: string) => (value === maxText || value === "" ? lastRangeStep : getNumberFromInputString(value, thousandSeparator)),
        [lastRangeStep, thousandSeparator]
    );
    const setMinValueFromRange = useCallback(
        (value: number) => (value === firstRangeStepIndex ? minText : getInputValueFromRange(language, value, steps, showPositiveLeadingSign)),
        [language, showPositiveLeadingSign, steps]
    );
    const setMaxValueFromRange = useCallback(
        (value: number) => (value === lastRangeStepIndex ? maxText : getInputValueFromRange(language, value, steps, showPositiveLeadingSign)),
        [language, lastRangeStepIndex, showPositiveLeadingSign, steps]
    );
    const isInputValueEqualMinDefaultTextValue = () => inputValue[0] === minText;
    const isInputValueEqualMaxDefaultTextValue = () => inputValue[1] === maxText;

    useEffect(() => {
        setInputValue([
            defaultMinValue === firstRangeStep ? minText : formatRangeSliderNumber({ language, value: defaultMinValue, showPositiveLeadingSign }),
            defaultMaxValue === lastRangeStep ? maxText : formatRangeSliderNumber({ language, value: defaultMaxValue, showPositiveLeadingSign }),
        ]);
        setRangeIndex([defaultMinRangeIndex, defaultMaxRangeIndex]);
    }, [
        defaultMaxRangeIndex,
        defaultMaxValue,
        defaultMinRangeIndex,
        defaultMinValue,
        firstRangeStep,
        language,
        lastRangeStep,
        selectedValues,
        showPositiveLeadingSign,
    ]);

    const onRangeChange = useCallback(
        (_: Event, value: number[]) => {
            if (rangeIndex[0] !== value[0] && value[0] < value[1]) {
                setRangeIndex(value as [number, number]);
                setInputValue(prev => [setMinValueFromRange(value[0]), prev[1]]);
            } else if (rangeIndex[1] !== value[1] && value[1] > value[0]) {
                setRangeIndex(value as [number, number]);
                setInputValue(prev => [prev[0], setMaxValueFromRange(value[1])]);
            }
        },
        [rangeIndex, setMaxValueFromRange, setMinValueFromRange]
    );

    const debouncedOnChange = useMemo(() => debounce((min: number, max: number) => onChange([min, max]), 300), [onChange]);

    const onRangeChangeEnd = useCallback(
        (event: Event | SyntheticEvent, value: number[]) => {
            if (isChangingRangeValueRef.current) {
                if (event.type === "keydown") {
                    return debouncedOnChange(getMinValue(setMinValueFromRange(value[0])), getMaxValue(setMaxValueFromRange(value[1])));
                }

                onChange([getMinValue(inputValue[0]), getMaxValue(inputValue[1])]);
            }
        },
        [getMaxValue, getMinValue, onChange, setMaxValueFromRange, setMinValueFromRange, debouncedOnChange, inputValue]
    );

    const onMinInputChange = (value: string) => {
        if (isInputValid(value)) {
            setInputValue([value, inputValue[1]]);
        }
    };

    const onMaxInputChange = (value: string) => {
        if (isInputValid(value)) {
            setInputValue([inputValue[0], value]);
        }
    };

    const handleMinInputBlur = () => {
        let minValue = getMinValue(inputValue[0]);
        const maxValue = getMaxValue(inputValue[1]);
        let minRangeIndex;
        let minInputValue;

        if (shouldNegateInputNumber(maxValue, defaultMaxValue)) {
            minValue *= -1;
        }

        if (minValue < maxValue && minValue > firstRangeStep) {
            minRangeIndex = getRangeIndexFromInput(minValue, steps);
            minInputValue = formatRangeSliderNumber({ language, value: minValue, showPositiveLeadingSign });
            onChange([minValue, maxValue]);
        } else {
            minRangeIndex = firstRangeStepIndex;
            minInputValue = minText;
            onChange([firstRangeStep, maxValue]);
        }

        setRangeIndex([minRangeIndex, rangeIndex[1]]);
        setInputValue([minInputValue, inputValue[1]]);
    };

    const handleMaxInputBlur = () => {
        const minValue = getMinValue(inputValue[0]);
        let maxValue = getMaxValue(inputValue[1]);
        let maxRangeIndex;
        let maxInputValue;

        if (shouldNegateInputNumber(maxValue, defaultMaxValue)) {
            maxValue *= -1;
        }

        if (maxValue > minValue && maxValue < lastRangeStep) {
            maxRangeIndex = getRangeIndexFromInput(maxValue, steps);
            maxInputValue = formatRangeSliderNumber({ language, value: maxValue, showPositiveLeadingSign });
            onChange([minValue, maxValue]);
        } else {
            maxRangeIndex = lastRangeStepIndex;
            maxInputValue = maxText;
            onChange([minValue, lastRangeStep]);
        }

        setRangeIndex([rangeIndex[0], maxRangeIndex]);
        setInputValue([inputValue[0], maxInputValue]);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>, enterHandler: () => void) => {
        if (event.key === KEY_ENTER) {
            enterHandler();
            rowRef.current?.focus();
        }
    };

    const handleInputValue = (validateInput: () => boolean, newInputValue: SetStateAction<[string, string]>) => {
        if (validateInput()) {
            setInputValue(newInputValue);
        }
    };

    const onThumbFocus = () => {
        isChangingRangeValueRef.current = true;
    };

    const onThumbBlur = () => {
        isChangingRangeValueRef.current = false;
    };

    return (
        <Box
            tabIndex={-1}
            data-test-id={KEY_FIGURE_TEST_ID}
            sx={{
                display: "grid",
                gridTemplateColumns: {
                    xs: "auto 4px auto",
                    sm: "auto auto 1fr auto",
                },
                gridTemplateAreas: {
                    xs: `"label label label" "leftInput divider rightInput" "slider slider slider"`,
                    sm: `"label leftInput slider rightInput"`,
                },
                columnGap: {
                    xs: 1,
                    sm: 3,
                },
                outline: "none",
                width: "100%",
                rowGap: 1,
                marginTop: {
                    xs: 2,
                    sm: 3,
                },
                marginBottom: 1,
                "&:first-of-type": {
                    marginTop: {
                        xs: 0,
                        sm: 1,
                    },
                },
                alignItems: "center",
            }}
            ref={rowRef}
        >
            <Box sx={{ gridArea: "label", width: { xs: "100%", sm: "150px" } }}>
                <Typography fontSize="sm">{label}</Typography>
            </Box>
            <Box sx={{ gridArea: "leftInput" }}>
                <RangeInput
                    ariaLabel={`${label} ${minText}`}
                    value={inputValue[0]}
                    shouldShowPercentageLabel={shouldShowPercentageLabel}
                    language={language}
                    onChange={onMinInputChange}
                    onBlur={handleMinInputBlur}
                    handleKeyDown={handleKeyDown}
                    handleOnFocus={() => handleInputValue(isInputValueEqualMinDefaultTextValue, ["", inputValue[1]])}
                />
            </Box>
            <Box
                sx={{
                    gridArea: "slider",
                    justifySelf: "center",
                    height: { xs: "32px", sm: "auto" },
                    width: { xs: "calc(100% - 16px)", sm: "100%", md: "100%" },
                    mt: { xs: 1, sm: 0 },
                }}
            >
                <Slider
                    value={rangeIndex}
                    min={firstRangeStepIndex}
                    max={lastRangeStepIndex}
                    step={STEP}
                    marks={showMarks && marks}
                    onChange={onRangeChange}
                    disableSwap={true}
                    sx={{
                        paddingY: {
                            xs: 0,
                            sm: 0.5,
                        },
                        pointerEvents: {
                            xs: "none",
                            sm: "auto",
                        },
                        touchAction: {
                            xs: "none",
                            sm: "auto",
                        },
                        "& .MuiSlider-thumb": {
                            pointerEvents: "auto",
                            touchAction: "auto",
                        },
                    }}
                    onFocus={onThumbFocus}
                    onBlur={onThumbBlur}
                    onChangeCommitted={onRangeChangeEnd}
                />
            </Box>
            <Box sx={{ gridArea: "rightInput" }}>
                <RangeInput
                    ariaLabel={`${label} ${maxText}`}
                    value={inputValue[1]}
                    shouldShowPercentageLabel={shouldShowPercentageLabel}
                    language={language}
                    onChange={onMaxInputChange}
                    onBlur={handleMaxInputBlur}
                    handleKeyDown={handleKeyDown}
                    handleOnFocus={() => handleInputValue(isInputValueEqualMaxDefaultTextValue, [inputValue[0], ""])}
                />
            </Box>
            <Box
                sx={{
                    gridArea: "divider",
                    width: "4px",
                    display: { xs: "block", sm: "none" },
                    height: "1px",
                    backgroundColor: "gray.800",
                    justifySelf: "center",
                }}
            />
        </Box>
    );
};

export const MemoizedRangeSliderWithInputs = memo(RangeSliderWithInputs, areSelectedInputValuesEqual);
